import React, { useRef } from 'react'
import styled from 'styled-components'
import { Variants, motion, useInView } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { IPricingPlan, IPricingTableData } from '@query'
import { SegmentedControl } from '@components'
import { CompactPricingSuccessBadge } from './CompactPricingSuccessBadge'
import { PricingCard } from './PricingCard'

interface Props {
  data: IPricingTableData
  interval: 'month' | 'year'
  onIntervalChange: (interval: 'month' | 'year') => void
  selectedPlanId: string | null
  isCheckingOut: boolean
  onSelectPlan: (plan: IPricingPlan) => void
}

const wrapperVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } }
}

export const PlanSelectionStep: React.FC<Props> = ({
  data,
  interval,
  onIntervalChange,
  selectedPlanId,
  isCheckingOut,
  onSelectPlan
}) => {
  const [t] = useTranslation()
  const cardsRowRef = useRef<HTMLDivElement>(null)
  const layoutReady = useInView(cardsRowRef, { once: true })

  return (
    <Wrapper variants={wrapperVariants} initial="hidden" animate="show">
      <motion.div variants={itemVariants}>
        <IntervalToggleWrapper>
          <SegmentedControl
            size="sm"
            value={interval}
            onUpdate={onIntervalChange}
            options={[
              { value: 'month', label: t('components.customPricingTable.monthly') },
              {
                value: 'year',
                label: (
                  <YearlyLabel>
                    {t('components.customPricingTable.yearly')}
                    <ToggleSaveBadge>
                      {t('components.customPricingTable.yearlyBadge')}
                    </ToggleSaveBadge>
                  </YearlyLabel>
                )
              }
            ]}
          />
        </IntervalToggleWrapper>
      </motion.div>

      <CardsRow ref={cardsRowRef}>
        {data.plans.map(plan => (
          <CardWrapper key={plan.id} variants={itemVariants} $layoutReady={layoutReady}>
            <PricingCard
              plan={plan}
              interval={interval}
              trialAvailable={data.trial_available}
              isSelected={selectedPlanId === plan.id}
              isCheckingOut={isCheckingOut && selectedPlanId === plan.id}
              isPopular={plan.id === 'plus'}
              onSelect={() => onSelectPlan(plan)}
            />
          </CardWrapper>
        ))}
      </CardsRow>
    </Wrapper>
  )
}

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl}rem;
`

const IntervalToggleWrapper = styled.div`
  min-width: 18rem;
`

const YearlyLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  white-space: nowrap;
`

/** Extra-compact badge so the segmented control row stays visually light. */
const ToggleSaveBadge = styled(CompactPricingSuccessBadge)`
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 600;

  & > div {
    padding: 0.03rem ${({ theme }) => theme.spacing.xxs}rem;
    line-height: 1.1;
  }
`

const CardsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex-wrap: wrap;
  width: 100%;
`

/* flex-basis trick: when the row is narrower than the max width of the cards the basis becomes a huge
   positive number, forcing each card onto its own row. Above the max width of the cards the basis is
   effectively 0 and flex-grow:1 distributes space equally — all cards side by side.
   The calc is only applied after mount so the parent has real dimensions to measure. */
const CardWrapper = styled(motion.div)<{ $layoutReady: boolean }>`
  flex: ${({ $layoutReady }) => ($layoutReady ? '1 1 calc((800px - 100%) * 999)' : '0 0 100%')};
  min-width: 0;
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`
